import { z } from 'zod';
import { siteCodeSchema } from '../sites';
import { timestampSchema } from '../timestamp';
import {
  maintenancePrioritySchema,
  notificationStatusSchema,
  notificationTypeSchema,
  symptomCodeSchema,
} from './enums';

export const NOTIFICATION_TEXT_MAX_LENGTH = 40;

export const NEO_ID_MAX_LENGTH = 12;
export const PLANNER_GROUP_MAX_LENGTH = 3;
export const REVISION_MAX_LENGTH = 8;
export const neoIdSchema = z.string().min(1).max(NEO_ID_MAX_LENGTH);

// ! 👇 Maps to S/4HANA CDS view I_MaintenanceNotificationItem
export const notificationItemSchema = z.object({
  MAINTENANCENOTIFICATIONITEM: z.string().max(4).optional(), // FENUM
  MAINTNOTIFITEMTEXT: z.string().max(40).optional(), // FETXT
  MAINTNOTIFDAMAGECODECATALOG: z.string().max(1).optional(), // FEKAT
  MAINTNOTIFICATIONDAMAGECODE: z.string().max(8).optional(), // FECOD
  MAINTNOTIFDAMAGECODEGROUP: z.string().max(8).optional(), // FEGRP
  MAINTNOTIFOBJPRTCODECATALOG: z.string().max(1).optional(), // OTKAT
  MAINTNOTIFOBJPRTCODE: z.string().max(4).optional(), // OTEIL
  MAINTNOTIFOBJPRTCODEGROUP: z.string().max(8).optional(), // OTGRP
  DEFECTCLASS: z.string().max(2).optional(), // FEQKLAS
  NUMBEROFDEFECTS: z.number().int().optional(), // ANZFEHLER
});
export type NotificationItem = z.infer<typeof notificationItemSchema>;

// ! 👇 Where a notification stands: `pending` until a manager validates it, `created` once validated (or `rejected`)
export enum NotificationSyncStatusEnum {
  PENDING = 'pending',
  CREATED = 'created',
  SYNCED = 'synced',
  FAILED = 'failed',
  REJECTED = 'rejected',
}
export const notificationSyncStatusSchema = z.enum(NotificationSyncStatusEnum);

export enum RejectReasonEnum {
  INSUFFICIENT_INFO = 'insufficientInfo',
  WRONG_LOCATION = 'wrongLocation',
  WRONG_TYPE = 'wrongType',
  DUPLICATE = 'duplicate',
  ALREADY_HANDLED = 'alreadyHandled',
  OTHER = 'other',
}
export const rejectReasonSchema = z.enum(RejectReasonEnum);

export const REJECT_COMMENT_MAX_LENGTH = 500;

export const rejectNotificationDTOSchema = z
  .object({
    REASON: rejectReasonSchema,
    COMMENT: z.string().trim().max(REJECT_COMMENT_MAX_LENGTH).default(''),
  })
  .refine(({ REASON, COMMENT }) => REASON !== RejectReasonEnum.OTHER || COMMENT !== '', {
    path: ['COMMENT'],
    message: 'A comment is required when the reason is "other"',
  });
export type RejectNotificationDTO = z.input<typeof rejectNotificationDTOSchema>;

export const notificationRejectionSchema = z.object({
  REASON: rejectReasonSchema,
  COMMENT: z.string(),
  REJECTEDBY: z.string(),
  REJECTEDDATETIME: timestampSchema,
});
export type NotificationRejection = z.infer<typeof notificationRejectionSchema>;

export const notificationModificationSchema = z.object({
  MODIFIEDBY: z.string(),
  MODIFIEDDATETIME: timestampSchema,
});
export type NotificationModification = z.infer<typeof notificationModificationSchema>;

// ! 👇 Maps to S/4HANA CDS views I_MaintenanceNotification + I_PmNotifMaintenanceData.
// Lengths (CHARn) and mandatory fields follow the mapping file with HIP.
export const notificationFieldsSchema = z.object({
  MAINTENANCENOTIFICATION: z.string().max(12).optional(), // QMNUM — assigned by SAP once synced
  NOTIFICATIONTYPE: notificationTypeSchema, // QMART
  MAINTPRIORITY: maintenancePrioritySchema, // PRIOK
  MAINTPRIORITYTYPE: z.literal('ZA').default('ZA'), // ARTPR
  NOTIFPROCESSINGPHASE: z.string().max(1).optional(), // PHASE — assigned by SAP
  CREATEDBYUSER: neoIdSchema, // ERNAM
  REPORTEDBYUSER: neoIdSchema, // QMNAM
  CREATIONDATE: z.iso.date(), // ERDAT
  CREATIONTIME: z.iso.time().optional(), // ERZEIT
  NOTIFICATIONTEXT: z.string().min(1).max(NOTIFICATION_TEXT_MAX_LENGTH), // QMTXT
  MAINTNOTIFLONGTEXT: z.string().optional(), // via READ_TEXT
  MAINTENANCEORDER: z.string().max(12).optional(), // AUFNR — assigned by SAP
  REQUIREDSTARTDATE: z.iso.date().optional(), // STRMN
  REQUIREDSTARTTIME: z.iso.time().optional(), // STRUR
  REQUIREDENDDATE: z.iso.date().optional(), // LTRMN
  REQUIREDENDTIME: z.iso.time().optional(), // LTRUR
  MAINTNOTIFICATIONCODE: symptomCodeSchema, // QMCOD — mandatory in SAP
  MAINTNOTIFICATIONCODEGROUP: z.literal('SYMPTOM2').default('SYMPTOM2'), // QMGRP
  FUNCTIONALLOCATION: z.string().min(1).max(30), // TPLNR — mandatory in SAP
  RELEVANTFUNCTIONALLOCATION: z.string().max(30).optional(), // BTPLN
  RELEVANTEQUIPMENT: z.string().max(18).optional(), // BEQUI
  ASSEMBLY: z.string().max(40).optional(), // BAUTL
  MAINTENANCEPLANNERGROUP: z.string().max(PLANNER_GROUP_MAX_LENGTH).optional(), // INGRP
  MAINTENANCEREVISION: z.string().max(REVISION_MAX_LENGTH).optional(), // REVNR
  MAINTENANCEOBJECTISDOWN: z.boolean().default(false), // MSAUS
  MALFUNCTIONSTARTDATE: z.iso.date().optional(), // AUSVN
  MALFUNCTIONSTARTTIME: z.iso.time().optional(), // AUZTV
  MALFUNCTIONENDDATE: z.iso.date().optional(), // AUSBS
  MALFUNCTIONENDTIME: z.iso.time().optional(), // AUZTB
  MAINTNOTIFSTATUS: notificationStatusSchema.optional(), // STAT — assigned by SAP
  ITEMS: z.array(notificationItemSchema).default([]),

  // ! 👇 App-local fields — no SAP equivalent
  SITE: siteCodeSchema,
  PREDIAGNOSIS: z.string().optional(), // TODO: This will be concatenated into a single fields for SAP -> define a pattern after
  REJECTION: notificationRejectionSchema.optional(),
  MODIFICATION: notificationModificationSchema.optional(),
  SYNCERROR: z.object({ CODE: z.string().optional(), MESSAGE: z.string() }).optional(), // why SAP refused it // TODO: ?? Is this necessary s
});

// DTO: request body of POST /notifications.
export const createNotificationDTOSchema = notificationFieldsSchema
  .omit({
    MAINTENANCENOTIFICATION: true,
    NOTIFPROCESSINGPHASE: true,
    MAINTENANCEORDER: true,
    MAINTNOTIFSTATUS: true,
    CREATEDBYUSER: true,
    CREATIONDATE: true,
    CREATIONTIME: true,
    MAINTPRIORITYTYPE: true,
    MAINTNOTIFICATIONCODEGROUP: true,
    REJECTION: true,
    MODIFICATION: true,
    SYNCERROR: true,
  })
  .extend({
    ID: z.uuid(), // generated by the client once per notification, so a retry of the same request is safe
  });

export type CreateNotificationDTO = z.input<typeof createNotificationDTOSchema>;

// Domain model: notification stored in our database
export const maintenanceNotificationSchema = notificationFieldsSchema.extend({
  ID: z.uuid(),
  SYNCSTATUS: notificationSyncStatusSchema,
  CREATIONDATETIME: timestampSchema,
  LASTCHANGEDATETIME: timestampSchema,
});

export type MaintenanceNotification = z.infer<typeof maintenanceNotificationSchema>;

// GET /notifications: `cursor` is absent on the last page.
export const notificationPageSchema = z.object({
  items: z.array(maintenanceNotificationSchema),
  cursor: z.string().optional(),
});
export type NotificationPage = z.infer<typeof notificationPageSchema>;
